import { QueryStringBuilder } from '@/shared/services/queryStringBuilder';
import urljoin from 'url-join';
import { DateOption } from '../models';
import { config } from '@/config/config';
import axios from 'axios';
import authService from '@/authentication/authentication.service';

export interface ReportCsvDownloadResponse {
  data: Blob;
  fileName: string;
}

class ReportDownloaderService {
  private readonly version = 'v1';

  public getFlowsReportCsv(
    workspaceId: string,
    flowId: string,
    dateOption: DateOption,
  ): Promise<ReportCsvDownloadResponse> {
    const query = new QueryStringBuilder()
      .add('flowId', flowId)
      .add('startDate', dateOption.getDateRange().start.toISOString())
      .add('endDate', dateOption.getDateRange().end.toISOString())
      .add('grouping', dateOption.group)
      .add('format', 'csv')
      .build();

    return this.send(urljoin(config().flowsHost, this.version, workspaceId, `combined-reports`) + query);
  }

  public getMessagingReportCsv(workspaceId: string, dateOption: DateOption): Promise<ReportCsvDownloadResponse> {
    const query = new QueryStringBuilder()
      .add('startDate', dateOption.getDateRange().start.toISOString())
      .add('endDate', dateOption.getDateRange().end.toISOString())
      .add('grouping', dateOption.group)
      .add('format', 'csv')
      .build();

    return this.send(urljoin(config().messagingHost, this.version, workspaceId, `reports`) + query);
  }

  public getNLPReportCsv(modelId: string, dateOption: DateOption): Promise<ReportCsvDownloadResponse> {
    const query = new QueryStringBuilder()
      .add('startDate', dateOption.getDateRange().start.toISOString())
      .add('endDate', dateOption.getDateRange().end.toISOString())
      .add('grouping', dateOption.group)
      .add('format', 'csv')
      .build();

    return this.send(urljoin(config().nlpHost, this.version, modelId, `reports`) + query);
  }

  public async getUsageReportCsv(workspaceId: string, dateOption: DateOption): Promise<ReportCsvDownloadResponse> {
    const query = new QueryStringBuilder()
      .add('startDate', dateOption.getDateRange().start.toISOString())
      .add('endDate', dateOption.getDateRange().end.toISOString())
      .add('format', 'csv')
      .build();

    return this.send(urljoin(config().usageHost, this.version, 'workspaces', workspaceId, `reports`) + query);
  }

  private async send(url: string) {
    const token = await authService.getToken();

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Access-Control-Expose-Headers': 'Content-Disposition',
      },
      responseType: 'blob',
    });

    const fileName = this.getFilenameFromHeader(response.headers['content-disposition']);

    return {
      data: response.data,
      fileName,
    };
  }

  private getFilenameFromHeader(header: string): string {
    const regex = /filename=(.*);/;
    const match = regex.exec(header);

    if (match) {
      return match[1];
    }

    return '';
  }
}

export const reportDownloaderService = new ReportDownloaderService();
